<template>
  <section class="staff-grid4-block dept-modern__section">
    <div class="dept-modern">
      <div class="staff-grid4-block__card dept-modern__card">
        <header v-if="heading" class="staff-grid4-block__header">
          <h2 class="staff-grid4-block__title dept-modern__title">{{ heading }}</h2>
          <p v-if="subheading" class="staff-grid4-block__subtitle">{{ subheading }}</p>
        </header>

        <div v-if="loadError" class="staff-grid4-block__error">
          Не удалось загрузить список специалистов
        </div>
        <AppLoader v-else-if="loading" />
        <StaffGrid4PeopleList
          v-else-if="peopleList.length"
          :people_list="peopleList"
          :open-photo-on-click="true"
          :show-detail-link="false"
          @open-photo="openPersonModal"
        />

        <footer v-if="showAllLink" class="staff-grid4-block__footer">
          <router-link :to="allSpecialistsLink" class="staff-grid4-block__all-link">
            <span>{{ allSpecialistsText }}</span>
            <img src="../../assets/svg/arrow-right.svg" alt="" class="staff-grid4-block__all-arrow">
          </router-link>
        </footer>
      </div>
    </div>

    <StaffPersonModal
      v-if="selectedPerson"
      :person="selectedPerson"
      @close="closePersonModal"
    />
  </section>
</template>

<script>
import DepartmentAPi from "@/api/department";
import StaffGrid4PeopleList from "@/components/People/StaffGrid4PeopleList.vue";
import StaffPersonModal from "@/components/People/StaffPersonModal.vue";
import AppLoader from "@/components/Loader.vue";

function extractEmployeeList(res) {
  const body = res && res.data !== undefined ? res.data : res;
  if (Array.isArray(body)) {
    return body;
  }
  if (body && body.status === "SUCCESS" && Array.isArray(body.result)) {
    return body.result;
  }
  return [];
}

const STAFF_GRID4_MAX = 8;

export default {
  name: "AppStaffGrid4Block",
  components: { StaffGrid4PeopleList, StaffPersonModal, AppLoader },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: true,
      loadError: false,
      peopleList: [],
      selectedPerson: null,
    };
  },
  computed: {
    heading() {
      const h = this.data && this.data.heading;
      return h != null && String(h).trim() ? String(h).trim() : "";
    },
    subheading() {
      const s = this.data && this.data.subheading;
      return s != null && String(s).trim() ? String(s).trim() : "";
    },
    allSpecialistsText() {
      const t = this.data && this.data.allSpecialistsText;
      return t != null && String(t).trim() ? String(t).trim() : "Все специалисты";
    },
    allSpecialistsLink() {
      const query = {};
      const subName = String(this.data.subDepartmentName || "").trim();
      if (subName) {
        query.subDepartment = subName;
      }
      return {
        path: "/about/specialists",
        query,
      };
    },
    showAllLink() {
      return this.data && this.data.showAllSpecialistsLink !== false;
    },
  },
  watch: {
    data: {
      deep: true,
      handler() {
        this.loadPeople();
      },
    },
  },
  mounted() {
    this.loadPeople();
  },
  methods: {
    openPersonModal(person) {
      if (!person) {
        return;
      }
      this.selectedPerson = person;
    },
    closePersonModal() {
      this.selectedPerson = null;
    },

    normalizePerson(emp) {
      if (!emp) {
        return null;
      }
      const rawId = emp.id != null ? emp.id : emp._id;
      if (rawId === undefined || rawId === null || String(rawId).trim() === "") {
        return null;
      }
      return {
        ...emp,
        id: String(rawId).trim(),
        opening: emp.openingHours || emp.opening || "",
      };
    },

    orderPeopleByIds(people, ids) {
      const byId = new Map(
        (people || [])
          .map((p) => this.normalizePerson(p))
          .filter(Boolean)
          .map((p) => [p.id, p])
      );
      return (ids || [])
        .map((id) => byId.get(String(id).trim()))
        .filter(Boolean)
        .slice(0, STAFF_GRID4_MAX);
    },

    async loadPeople() {
      const useManual = this.data.useManualList && Array.isArray(this.data.manualPeople) && this.data.manualPeople.length;
      if (useManual) {
        this.loading = false;
        this.loadError = false;
        this.peopleList = this.data.manualPeople
          .map((p, idx) => ({
            id: p.id || `manual-${idx}`,
            name: p.name || '',
            position: p.position || '',
            qualificationCategory: p.qualificationCategory || '',
            image: p.image || '',
            imageThumb: p.image || '',
          }))
          .slice(0, STAFF_GRID4_MAX);
        return;
      }

      const selectedIds = (this.data.employeeIds || [])
        .map((id) => String(id).trim())
        .filter(Boolean)
        .slice(0, STAFF_GRID4_MAX);

      if (!selectedIds.length) {
        this.loading = false;
        this.loadError = false;
        this.peopleList = [];
        return;
      }

      this.loading = true;
      this.loadError = false;
      try {
        const res = await DepartmentAPi.postEmployeesByIds(selectedIds);
        const list = extractEmployeeList(res);
        this.peopleList = this.orderPeopleByIds(list, selectedIds);
      } catch (e) {
        this.loadError = true;
        this.peopleList = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
$green: #66cc66;
$green-text: #336633;
$title-color: #3d4654;

.staff-grid4-block {
  width: 100%;
  box-sizing: border-box;
}

.staff-grid4-block__card {
  padding: 28px 32px 32px;
  box-sizing: border-box;
  overflow: hidden;
}

.staff-grid4-block :deep(.staff-grid4-item) {
  border-color: #eef2ee;
  box-shadow: none;
}

.staff-grid4-block__header {
  margin-bottom: 20px;
}

.staff-grid4-block__title {
  margin: 0;
  font-size: 28px;
}

.staff-grid4-block__subtitle {
  margin: 8px 0 0;
  font-family: "Circe", sans-serif;
  font-size: 16px;
  color: #6b7280;
}

.staff-grid4-block__error {
  padding: 16px 0;
  font-family: "Circe", sans-serif;
  font-size: 18px;
  color: #6b7280;
}

.staff-grid4-block__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
}

.staff-grid4-block__all-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 22px;
  border: 1px solid $green;
  border-radius: 999px;
  text-decoration: none;
  font-family: "Circe", sans-serif;
  font-size: 15px;
  color: $green;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(102, 204, 102, 0.06);
  }
}

.staff-grid4-block__all-arrow {
  width: 14px;
  height: 14px;
}
</style>
